/**
 * BBS Personalized Football Water Bottle — Live Preview
 * Product: /products/personalized-football-water-bottle
 *
 * As the customer types their name, it appears over the football design on
 * the bottle in real time. Config calibrated 2026-07-10.
 */
(function () {
  'use strict';

  /* ── Calibrated config ────────────────────────────────────────────────────── */
  var LINE_TOP_FRAC    = 0.5546;
  var LINE_BOTTOM_FRAC = 0.5735;
  var TEXT_WIDTH_FRAC  = 0.1077;
  var TEXT_CENTER_X_FRAC = 0.51; // manual nudge 2026-07-10: 0.50 too left, 0.52 too right, splitting the difference
  var FONT_SCALE = 0.92; // manual nudge 2026-07-10: text hanging slightly outside the banner, shrink a touch
  var DESIGN_ROTATION  = 0.0000;
  var LETTER_FONT      = '"Century Gothic","Apple Gothic",sans-serif';
  var LETTER_COLOR     = '#aba39c';
  var BOLD_TEXT        = false;
  var BEND             = 0;

  var CLEAN_SRC = 'https://cdn.shopify.com/s/files/1/0370/6771/8794/files/calibrator-image-clean_5.png?v=1783525249';

  /* ── State ─────────────────────────────────────────────────────────────────── */
  var inputEl = null;
  var overlay = null;
  var canvas  = null;
  var ctx     = null;
  var showing = false;

  var img    = new Image();
  var loaded = false;
  img.crossOrigin = 'anonymous';
  img.onload  = function () { loaded = true; repaint(); };
  img.onerror = function () {
    var fb = new Image();
    fb.onload = function () { img = fb; loaded = true; repaint(); };
    fb.src = CLEAN_SRC;
  };
  img.src = CLEAN_SRC;

  /* ── Overlay ────────────────────────────────────────────────────────────────── */
  function buildOverlay(container) {
    if (overlay) return;
    if (getComputedStyle(container).position === 'static') container.style.position = 'relative';

    overlay = document.createElement('div');
    overlay.id = 'bbs-football-water-bottle-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = [
      'position:absolute', 'top:0', 'left:0', 'right:0', 'bottom:0',
      'z-index:20', 'opacity:0', 'transition:opacity .22s ease',
      'pointer-events:none', 'overflow:hidden',
    ].join(';');

    canvas = document.createElement('canvas');
    canvas.style.cssText = 'display:block;width:100%;height:100%;';
    overlay.appendChild(canvas);
    container.appendChild(overlay);
    ctx = canvas.getContext('2d');
  }

  /* ── Parabolic arc text (matches calibrator algorithm) ────────────────────── */
  function arcLen(A, t) {
    if (Math.abs(t) < 0.001) return t;
    var n = 20, dt = t / n, sum = 0;
    for (var k = 0; k < n; k++) {
      var m = (k + 0.5) * dt;
      sum += Math.sqrt(1 + 4 * A * A * m * m);
    }
    return sum * dt;
  }
  function arcToX(A, s) {
    if (Math.abs(s) < 0.001) return s;
    var x = s;
    for (var it = 0; it < 12; it++) {
      var dx = (arcLen(A, x) - s) / Math.sqrt(1 + 4 * A * A * x * x);
      x -= dx;
      if (Math.abs(dx) < 0.01) break;
    }
    return x;
  }
  function drawArcText(text, cx, cy, bend) {
    var chars = text.split('');
    var cws = chars.map(function (c) {
      var w = ctx.measureText(c).width;
      return c === ' ' ? w * 0.55 : w;
    });
    var totalW = cws.reduce(function (a, b) { return a + b; }, 0);
    if (totalW < 1) return;
    var halfW = totalW / 2;
    var archH = halfW * Math.abs(bend) / 100 * 1.0;
    var A = (bend > 0 ? -1 : 1) * archH / (halfW * halfW);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    var sCursor = -totalW / 2;
    for (var i = 0; i < chars.length; i++) {
      var cw = cws[i];
      var sCenter = sCursor + cw / 2;
      var xOff = arcToX(A, sCenter);
      ctx.save();
      ctx.translate(cx + xOff, cy + A * xOff * xOff);
      ctx.rotate(Math.atan(2 * A * xOff));
      ctx.fillText(chars[i], 0, 0);
      ctx.restore();
      sCursor += cw;
    }
  }

  /* ── Repaint ────────────────────────────────────────────────────────────────── */
  function repaint() {
    if (!canvas || !ctx || !loaded) return;

    var name = inputEl ? inputEl.value.trim().toUpperCase() : '';

    if (!name) {
      if (showing) { overlay.style.opacity = '0'; showing = false; }
      return;
    }
    if (!showing) { overlay.style.opacity = '1'; showing = true; }

    var rect = canvas.getBoundingClientRect();
    var dpr  = window.devicePixelRatio || 1;
    var W = Math.round(rect.width  * dpr) || 600;
    var H = Math.round(rect.height * dpr) || 600;
    if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }

    ctx.clearRect(0, 0, W, H);

    /* Cover-fill the clean bottle image */
    var iW = img.naturalWidth  || W;
    var iH = img.naturalHeight || H;
    var sc = (iW / iH > W / H) ? H / iH : W / iW;
    var dW = iW * sc, dH = iH * sc;
    var dX = (W - dW) / 2, dY = (H - dH) / 2;
    ctx.drawImage(img, dX, dY, dW, dH);

    /* Text zone from calibrator */
    var lineTopY    = dY + LINE_TOP_FRAC    * dH;
    var lineBottomY = dY + LINE_BOTTOM_FRAC * dH;
    var lineGap     = lineBottomY - lineTopY;
    var textCY      = (lineTopY + lineBottomY) / 2;
    var textCX      = dX + TEXT_CENTER_X_FRAC * dW;
    var maxW        = dW * TEXT_WIDTH_FRAC;

    /* Font sizing: fill the band vertically, constrain by width */
    var fs = lineGap / 0.72 * FONT_SCALE;
    var fontStr = (BOLD_TEXT ? 'bold ' : '') + fs + 'px ' + LETTER_FONT;
    ctx.font = fontStr;
    ctx.fillStyle = LETTER_COLOR;
    ctx.shadowColor = 'rgba(0,0,0,0.12)';
    ctx.shadowBlur  = Math.max(1, fs * 0.03);

    /* Shrink if name overflows width */
    var testW = name.split('').reduce(function (sum, c) {
      var w = ctx.measureText(c).width;
      return sum + (c === ' ' ? w * 0.55 : w);
    }, 0);
    if (testW > maxW) {
      fs = fs * maxW / testW;
      ctx.font = (BOLD_TEXT ? 'bold ' : '') + fs + 'px ' + LETTER_FONT;
    }

    /* Apply design rotation if any */
    if (DESIGN_ROTATION !== 0) {
      ctx.save();
      ctx.translate(textCX, textCY);
      ctx.rotate(DESIGN_ROTATION);
      ctx.translate(-textCX, -textCY);
    }

    if (Math.abs(BEND) < 1) {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(name, textCX, textCY);
    } else {
      drawArcText(name, textCX, textCY, BEND);
    }

    if (DESIGN_ROTATION !== 0) ctx.restore();
  }

  /* ── Find BOLD name input ───────────────────────────────────────────────────── */
  function findInput() {
    var selectors = [
      'input[name="properties[Name]"]',
      'input[name="properties[name]"]',
      'input[name="properties[Player Name]"]',
      'input[name="properties[Personalization]"]',
      'input[name="properties[Text]"]',
      'input[name="properties[Custom Text]"]',
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) return el;
    }
    var found = null;
    document.querySelectorAll('label').forEach(function (lbl) {
      if (found) return;
      var t = lbl.textContent.toLowerCase();
      if (t.indexOf('name') > -1 || t.indexOf('personali') > -1 || t.indexOf('text') > -1) {
        found = lbl.control || document.getElementById(lbl.htmlFor);
        if (found && found.tagName !== 'INPUT') found = null;
      }
    });
    return found;
  }

  /* ── Init ────────────────────────────────────────────────────────────────────── */
  function init() {
    var container = document.querySelector('[data-product-gallery]')
      || document.querySelector('.product_gallery')
      || document.querySelector('.product-gallery')
      || document.querySelector('.product__media-wrapper')
      || document.querySelector('[class*="product"][class*="gallery"]')
      || document.querySelector('[class*="product"][class*="media"]');
    if (!container) return;

    buildOverlay(container);

    var tries = 0;
    var poll = setInterval(function () {
      inputEl = findInput();
      if (inputEl) {
        clearInterval(poll);
        inputEl.addEventListener('input',  repaint);
        inputEl.addEventListener('change', repaint);
        setTimeout(repaint, 150);
      } else if (++tries > 80) clearInterval(poll);
    }, 100);

    window.addEventListener('resize', function () { if (showing) repaint(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(init, 400); });
  } else {
    setTimeout(init, 400);
  }

})();
